import React, { useCallback, useEffect, useMemo, useState } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const TOKEN_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function formatEventWhen(ev) {
  if (!ev) return '—';
  const d = ev.eventDate && /^\d{4}-\d{2}-\d{2}$/.test(ev.eventDate) ? ev.eventDate : null;
  const t = (ev.time || '').trim() || '—';
  return d ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)} klo ${t}` : t;
}

export default function EventPromoModal({ user, token, onClose }) {
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const uid = user?.id != null ? String(user.id).replace(/n$/, '') : '';
  const tokenOk = token && TOKEN_RE.test(String(token).trim());

  const loadStatus = useCallback(() => {
    if (!uid || !tokenOk) return;
    setStatusError(null);
    fetch(`${API}/api/event-promo/status?token=${encodeURIComponent(String(token).trim())}&userId=${encodeURIComponent(uid)}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || data.message || `Virhe ${r.status}`);
        return data;
      })
      .then(setStatus)
      .catch((e) => setStatusError(e.message || 'Tilaa ei voitu ladata'));
  }, [uid, token, tokenOk]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (!uid) return;
    fetch(`${API}/api/events?createdByUserId=${encodeURIComponent(uid)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('fetch'))))
      .then((data) => setEvents(Array.isArray(data) ? data : []))
      .catch(() => setEvents([]));
  }, [uid]);

  const owned = useMemo(() => {
    return (events || []).filter((e) => {
      const c = e.createdByUserId != null ? String(e.createdByUserId).replace(/n$/, '') : '';
      return c === uid;
    });
  }, [events, uid]);

  const assign = () => {
    if (!uid || !tokenOk || !selectedId) return;
    setSaving(true);
    setSaveError(null);
    fetch(`${API}/api/event-promo/assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: uid, token: String(token).trim(), eventId: selectedId }),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.message || data.error || 'Virhe');
        return data;
      })
      .then(() => {
        loadStatus();
      })
      .catch((e) => setSaveError(e.message || 'Tallennus epäonnistui'))
      .finally(() => setSaving(false));
  };

  if (!tokenOk) {
    return (
      <div className="Modal-backdrop EventPromoModal-backdrop" role="presentation" onClick={onClose}>
        <div
          className="Modal EventPromoModal-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="event-promo-modal-title"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 id="event-promo-modal-title">Tapahtumamainos</h2>
          <p>Linkki on virheellinen tai vanhentunut.</p>
          <div className="Modal-actions">
            <button type="button" className="Button" onClick={onClose}>
              Sulje
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="Modal-backdrop EventPromoModal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="Modal EventPromoModal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="event-promo-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="event-promo-modal-title">Mainosta tapahtumaasi (24h)</h2>
        <p className="EventPromoModal-lead">
          Valitse tapahtuma, jota haluat näyttää etusivun mainospalkissa vuorokauden ajan, kun vuorosi tulee jonossa.
        </p>

        {statusError ? (
          <p className="Modal-error" role="alert">
            {statusError}
          </p>
        ) : null}
        {!status && !statusError ? <p>Ladataan…</p> : null}

        {status?.eventId ? (
          <p className="EventPromoModal-status">
            Valittu tapahtuma: <strong>{status.eventTitle || status.eventId}</strong>
            {status.onBoardNow ? (
              <>
                <br />
                <span className="EventPromoModal-queued">Mainos on tällä hetkellä (tai juuri ollut) mainospalkissa.</span>
              </>
            ) : status.queued ? (
              <>
                <br />
                <span className="EventPromoModal-queued">
                  Mainos on jonossa
                  {status.queuePlace != null ? ` (sijalla ${status.queuePlace})` : ''}. Saat viestin, kun se etenee.
                </span>
              </>
            ) : (
              <>
                <br />
                <span className="EventPromoModal-queued">
                  Valinta tallennettu. Mainos lisätään jonoon seuraavassa yöajossa (automaattinen käsittely).
                </span>
              </>
            )}
          </p>
        ) : null}

        {!status?.eventId ? (
          <>
            <label className="Forum-draft-field EventPromoModal-field">
              Oma tapahtuma
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                disabled={saving || owned.length === 0}
              >
                <option value="">— Valitse —</option>
                {owned.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.title || ev.id} ({formatEventWhen(ev)})
                  </option>
                ))}
              </select>
            </label>
            {owned.length === 0 ? (
              <p className="EventPromoModal-hint">Sinulla ei ole vielä tapahtumia. Luo tapahtuma hallintapaneelista.</p>
            ) : null}
            {saveError ? (
              <p className="Modal-error" role="alert">
                {saveError}
              </p>
            ) : null}
            <div className="Modal-actions">
              <button type="button" className="Button Button-secondary" onClick={onClose} disabled={saving}>
                Sulje
              </button>
              <button type="button" className="Button" onClick={assign} disabled={saving || !selectedId}>
                {saving ? 'Tallennetaan…' : 'Tallenna valinta'}
              </button>
            </div>
          </>
        ) : (
          <div className="Modal-actions">
            <button type="button" className="Button" onClick={onClose}>
              Sulje
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
